import { Stack, Typography } from '@mui/material';
import { CompanyRole, EnumCurrency, EnumProjectMilestoneStatus } from 'tdc-web-backend/enums/enums';
import {
  MilestoneDto,
  MilestoneSnakeDto,
  UpdateMilestoneDto,
} from 'tdc-web-backend/milestones/schemas';
import { Dispatch, SetStateAction, useState } from 'react';
import { TaskDto } from 'tdc-web-backend/tasks/schemas';
import { ProjectDto } from 'tdc-web-backend/projects/schemas';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import { useTranslation } from 'react-i18next';
import { primaryDark, primaryLight, secondaryPink } from '../../../../../utils/color';
import { ReactComponent as FlagIcon } from '../../../../../assets/icons/layout-icons/FlagIcon.svg';
import { ReactComponent as PencilIcon } from '../../../../../assets/icons/layout-icons/PencilIcon.svg';
import { ReactComponent as CalendarIcon } from '../../../../../assets/icons/layout-icons/CalendarIcon.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../../assets/icons/layout-icons/ArrowRight.svg';
import { ReactComponent as CalendarCheckIcon } from '../../../../../assets/icons/project-icons/CalendarCheckIcon.svg';
import CustomDropdown from '../../../../../components/custom-dropdown/CustomDropdown';
import { formatCurrency, formatDateFns } from '../../../../../utils/helpers';
import DynamicDescriptionAndReadMoreWithModal from '../../../../../components/dynamic-description-and-read-more-with-modal/DynamicDescriptionAndReadMoreWithModal';
import useUserCompany from '../../../../../utils/hooks/useUserCompany';
import { EndToEndDetailScreenTypeEnum } from '../../../../../utils/enums';
import RecurringMilestoneDropdown from '../Recurring/RecurringMilestoneDropdown';
import useReferenceOne from '../../../../../utils/hooks/crud-hooks/useReferenceOne';
import AmendContractModal from '../../../Contracts/modals/AmendContractModal';
import useUpdate from '../../../../../utils/hooks/crud-hooks/useUpdate';
import { useRefresh } from '../../../../../utils/hooks/crud-hooks/useRefresh';
import useMode from '../../../../../utils/hooks/useMode';
import { EnumMode } from '../../../../../utils/context/Mode.context';

interface GeneralMilestoneDetailsProps {
  milestoneData?: MilestoneDto;
  recurringMilestonesParent?: MilestoneSnakeDto | undefined | null;
  setIsFlagAndUnflagModalOpen: Dispatch<SetStateAction<boolean>>;
  setEntityType: Dispatch<SetStateAction<EndToEndDetailScreenTypeEnum>>;
  setEntityId: Dispatch<SetStateAction<string | null>>;
  setTaskId: Dispatch<SetStateAction<string | null>>;
  determineWhichTaskToShow: (tasks: TaskDto[]) => string | null;
}

const GeneralMilestoneDetails = ({
  milestoneData,
  recurringMilestonesParent,
  setIsFlagAndUnflagModalOpen,
  setEntityType,
  setEntityId,
  setTaskId,
  determineWhichTaskToShow,
}: GeneralMilestoneDetailsProps) => {
  const { mode } = useMode();
  const { t, i18n } = useTranslation();
  const isMilestoneFlagged = milestoneData?.buyerFlagged || milestoneData?.sellerFlagged;
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const refresh = useRefresh();

  const shouldShowButtonToUnflag = () => {
    if (mode === EnumMode.Client) {
      if (milestoneData?.buyerFlagged) return true;
      if (milestoneData?.sellerFlagged) return false;
    }

    if (mode === EnumMode.Supplier) {
      if (milestoneData?.buyerFlagged) return false;
      if (milestoneData?.sellerFlagged) return true;
    }

    return true;
  };
  const { data: projectData } = useReferenceOne<ProjectDto>({
    enabled: !!milestoneData?.project,
    resource: 'projects',
    id: milestoneData?.project,
  });

  const { mutate: update } = useUpdate<MilestoneDto, UpdateMilestoneDto>({
    resource: '/milestones',
  });

  const customProgressDropdownMenuItemContent = {
    key: 'progress',
    icon: <PlayCircleFilledWhiteIcon sx={{ color: primaryDark[500], width: 18, height: 18 }} />,
    text: t('common.start'),
    onClick: () =>
      update(
        {
          id: milestoneData?.id as string,
          suffix: 'set-in-progress',
        },
        {
          onSuccess: () => {
            refresh();
          },
        },
      ),
  };

  const customEditDropdownMenuItemContent = {
    key: 'edit',
    icon: <PencilIcon fill={primaryDark[500]} style={{ width: 18, height: 18 }} />,
    text: t('common.edit'),
    disabled: (() => {
      if (
        milestoneData?.status === EnumProjectMilestoneStatus.UnderReview ||
        milestoneData?.status === EnumProjectMilestoneStatus.Approved ||
        milestoneData?.status === EnumProjectMilestoneStatus.Completed ||
        projectData?.locked === true
      ) {
        return true;
      }

      return false;
    })(),
    onClick: () => setIsEditModalOpen(true),
  };

  const customFlagDropdownMenuItemContent = {
    key: isMilestoneFlagged ? t('common.unflag') : t('common.flag'),
    icon: <FlagIcon fill={primaryDark[500]} style={{ width: 18, height: 18 }} />,
    text: isMilestoneFlagged ? t('common.unflag') : t('common.flag'),
    disabled: (() => {
      // if milestone status is pending, disable the button
      if (
        milestoneData?.status === EnumProjectMilestoneStatus.Pending ||
        milestoneData?.status === EnumProjectMilestoneStatus.Completed ||
        projectData?.locked === true
      ) {
        return true;
      }

      // every else status enable the button so it can be flagged
      return false;
    })(),
    onClick: () => {
      setIsFlagAndUnflagModalOpen(true);
      setEntityType(EndToEndDetailScreenTypeEnum.Milestone);
      setEntityId(milestoneData?.id as string);
    },
  };

  const dropdownMenuItems = [];

  if (milestoneData?.status === EnumProjectMilestoneStatus.Pending) {
    dropdownMenuItems.push(customProgressDropdownMenuItemContent);
  }
  if (shouldShowButtonToUnflag()) {
    dropdownMenuItems.push(customFlagDropdownMenuItemContent);
  }
  dropdownMenuItems.push(customEditDropdownMenuItemContent);

  return (
    <>
      {/* edit milestone modal */}
      <AmendContractModal
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        projectId={milestoneData?.project}
        contractId={projectData?.latestContract as string}
      />
      <Stack spacing={6}>
        {/* name, start, end date, flagged indicator */}
        <Stack spacing={1.5}>
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={1} direction="row" alignItems="center" height="fit-content">
              <CustomDropdown
                menuItemsContents={dropdownMenuItems}
                dropdownSx={{
                  width: '150px',
                }}
              />

              <Typography variant="heading2" color="primaryDark.600">
                {milestoneData?.name}
              </Typography>
            </Stack>

            {/* flagged indicator & dropdown for recurring milestones */}
            <Stack spacing={1} direction="row" alignItems="center" height="40px">
              {isMilestoneFlagged && (
                <Stack
                  spacing={1}
                  direction="row"
                  alignItems="center"
                  sx={{
                    height: '100%',
                    bgcolor: 'secondaryPink.100',
                    px: 1.5,
                    py: 0.85,
                    borderRadius: '6px',
                  }}
                >
                  <FlagIcon fill={secondaryPink[700]} style={{ width: 20, height: 20 }} />

                  <Typography variant="heading4" color="secondaryPink.700">
                    {t('common.flagged')}
                  </Typography>
                </Stack>
              )}

              {recurringMilestonesParent && (
                <RecurringMilestoneDropdown
                  milestone={recurringMilestonesParent}
                  setTaskId={setTaskId}
                  showText
                  determineWhichTaskToShow={determineWhichTaskToShow}
                />
              )}
            </Stack>
          </Stack>

          <Stack spacing={1.5} alignItems="center" direction="row">
            <CalendarIcon fill={primaryDark[500]} style={{ width: 20, height: 20 }} />

            {milestoneData?.start && (
              <Typography color="primaryDark.600" fontWeight={600}>
                {formatDateFns(milestoneData.start, i18n.language, '-').toUpperCase()}
              </Typography>
            )}

            <ArrowRightIcon fill={primaryLight[500]} style={{ width: 16, height: 16 }} />

            <CalendarCheckIcon fill={primaryDark[500]} style={{ width: 20, height: 20 }} />

            {milestoneData?.end && (
              <Typography color="primaryDark.600" fontWeight={600}>
                {formatDateFns(milestoneData.end, i18n.language, '-').toUpperCase()}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* description & budget */}
        <Stack direction="row" justifyContent="space-between">
          <DynamicDescriptionAndReadMoreWithModal
            description={milestoneData?.summary as string}
            sx={{ width: '60%' }}
            title={t('projects.milestoneDescription')}
          />

          <Stack spacing={2}>
            <Typography variant="heading4" color="primaryDark.500">
              {t('common.budget')}
            </Typography>

            {milestoneData && milestoneData.budget && milestoneData.currency && (
              <Typography
                variant="heading4"
                color="primaryDark.600"
                fontSize={{ md: '1.45rem', lg: '1.75rem' }}
              >
                {formatCurrency(
                  milestoneData?.budget as number,
                  milestoneData?.currency as EnumCurrency,
                ) ?? '-'}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default GeneralMilestoneDetails;
